import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, RefreshCw, CheckCircle2, XCircle, Send, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending_review: { text: 'Pending review', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved_seeding: { text: 'Building…', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  shipped: { text: 'Shipped', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  ship_failed: { text: 'Ship failed', color: 'bg-rose-100 text-rose-800 border-rose-300' },
  rejected: { text: 'Rejected', color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const TIER_COLOR = {
  '$19': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  '$29': 'bg-violet-50 text-violet-700 border-violet-200',
  '$39': 'bg-amber-50 text-amber-700 border-amber-200',
  '$19_monthly': 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function AdminHouseProductsWeekly() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [proposals, setProposals] = useState([]);
  const [activeWeek, setActiveWeek] = useState(null);
  const [activeProposal, setActiveProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [approving, setApproving] = useState(false);

  const authHeaders = () => {
    const sess = localStorage.getItem('token');
    return sess ? { Authorization: `Bearer ${sess}` } : {};
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/house-products/proposals`, { headers: authHeaders() });
      setProposals(res.data.proposals || []);
      const weekParam = Number(searchParams.get('week'));
      const target = weekParam || (res.data.proposals?.[0]?.week);
      if (target) setActiveWeek(target);
    } catch (e) {
      toast.error('Failed to load proposals (admin only)');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const loadWeek = useCallback(async (week) => {
    if (!week) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/house-products/proposals/${week}`, { headers: authHeaders() });
      setActiveProposal(res.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setActiveProposal(null);
      } else {
        toast.error('Failed to load this week');
      }
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadWeek(activeWeek); }, [activeWeek, loadWeek]);

  const triggerNow = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/house-products/proposals/trigger-now`, {}, { headers: authHeaders() });
      toast.success(`Brainstorm complete — week ${res.data.week}`);
      await loadList();
      setActiveWeek(res.data.week);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Brainstorm failed');
    } finally {
      setTriggering(false);
    }
  };

  const approve = async () => {
    if (!activeProposal) return;
    if (!window.confirm(`Ship all 12 products for week ${activeProposal.week}? This kicks off the full build pipeline (5-10 min).`)) return;
    setApproving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/house-products/proposals/${activeProposal.week}/approve`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Build started — week ${res.data.week}. You'll receive an email when complete.`);
      await loadWeek(activeProposal.week);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    if (!activeProposal) return;
    if (!window.confirm(`Reject week ${activeProposal.week}'s slate?`)) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/house-products/proposals/${activeProposal.week}/reject`,
        {},
        { headers: authHeaders() }
      );
      toast.success('Rejected');
      await loadWeek(activeProposal.week);
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  const persona = activeProposal?.proposal?.storefront_persona || {};
  const products = activeProposal?.proposal?.products || [];
  const statusInfo = STATUS_LABEL[activeProposal?.status] || STATUS_LABEL.pending_review;

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="admin-house-products-weekly">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-amber-500" />
              Weekly House Products
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Each Monday at 6:30 AM Pacific, the cron generates 12 new product ideas + a fresh storefront persona,
              runs TM-aware naming checks, and emails you this approval link. Approve to ship.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={triggerNow}
              disabled={triggering}
              variant="outline"
              data-testid="trigger-brainstorm-btn"
            >
              {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Brainstorm now
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {!loading && proposals.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center bg-white">
            <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-700 font-medium mb-1">No weekly proposals yet</p>
            <p className="text-sm text-slate-500 mb-4">The Monday 06:30 PT cron will create one automatically. Or click "Brainstorm now" to generate a slate right away.</p>
            <Button onClick={triggerNow} disabled={triggering} data-testid="brainstorm-now-empty">
              {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate first slate
            </Button>
          </div>
        )}

        {!loading && proposals.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar — list of past weeks */}
            <aside className="lg:col-span-3 space-y-1.5" data-testid="weekly-sidebar">
              {proposals.map((p) => (
                <button
                  key={p.week}
                  onClick={() => { setActiveWeek(p.week); setSearchParams({ week: String(p.week) }); }}
                  className={`w-full text-left px-3 py-2.5 rounded border transition ${
                    activeWeek === p.week
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700'
                  }`}
                  data-testid={`week-tab-${p.week}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">Week {p.week}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_LABEL[p.status]?.color || 'bg-slate-100'}`}>
                      {STATUS_LABEL[p.status]?.text || p.status}
                    </span>
                  </div>
                  <div className="text-xs opacity-70 mt-0.5">
                    {p.proposal?.storefront_persona?.name || '—'}
                  </div>
                </button>
              ))}
            </aside>

            {/* Main panel */}
            <main className="lg:col-span-9">
              {!activeProposal && (
                <div className="border border-slate-200 rounded-lg p-12 text-center bg-white text-slate-500">
                  Select a week to review.
                </div>
              )}

              {activeProposal && (
                <>
                  {/* Status banner */}
                  <div className={`border rounded-lg p-4 mb-5 flex items-center justify-between ${statusInfo.color}`}>
                    <div>
                      <div className="text-xs uppercase tracking-wider opacity-70 font-bold">Status</div>
                      <div className="font-semibold">{statusInfo.text}</div>
                    </div>
                    <div className="text-xs opacity-80">
                      Generated: {activeProposal.created_at?.slice(0, 16).replace('T', ' ')}
                      {activeProposal.shipped_at && (
                        <div>Shipped: {activeProposal.shipped_at.slice(0, 16).replace('T', ' ')}</div>
                      )}
                    </div>
                  </div>

                  {activeProposal.ship_error && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-5 text-sm text-rose-800 flex gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Ship error</div>
                        <div className="font-mono text-xs">{activeProposal.ship_error}</div>
                      </div>
                    </div>
                  )}

                  {/* Storefront persona */}
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-5 mb-5">
                    <div className="text-xs uppercase tracking-wider opacity-60 font-bold mb-1">Storefront persona (week {activeProposal.week})</div>
                    <div className="text-2xl font-bold">{persona.name || '—'}</div>
                    <div className="text-sm opacity-80 mt-1">{persona.tagline || ''}</div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="bg-white/5 rounded p-2">
                        <div className="opacity-60 uppercase">TM notes</div>
                        <div className="mt-0.5">{persona.tm_risk_notes || '—'}</div>
                      </div>
                      <div className="bg-white/5 rounded p-2">
                        <div className="opacity-60 uppercase">Alt name</div>
                        <div className="mt-0.5">{persona.alt_name || '—'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Products grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5" data-testid="weekly-products-grid">
                    {products.map((p, i) => (
                      <div
                        key={i}
                        className="border border-slate-200 rounded-lg p-4 bg-white hover:border-slate-400 transition"
                        data-testid={`weekly-product-card-${i}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs text-slate-400 font-mono">{String(i + 1).padStart(2, '0')}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded border font-mono ${TIER_COLOR[p.price_tier] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                            {p.price_tier}
                          </span>
                        </div>
                        <div className="font-bold text-slate-900">{p.name}</div>
                        <div className="text-sm text-slate-600 mt-1 leading-snug">{p.tagline}</div>
                        <div className="text-xs text-slate-500 mt-2 italic">Pain: {p.pain}</div>
                        <div className="mt-2 text-[11px] text-slate-500">
                          <span className="font-semibold">Category:</span> {p.category_slug}
                        </div>
                        {p.tm_risk_notes && p.tm_risk_notes !== 'no obvious conflicts' && (
                          <div className="mt-2 text-[10px] bg-amber-50 text-amber-800 px-2 py-1 rounded border border-amber-200">
                            ⚠ TM: {p.tm_risk_notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Action bar */}
                  {activeProposal.status === 'pending_review' && (
                    <div className="sticky bottom-4 bg-white border border-slate-300 rounded-lg p-4 shadow-lg flex items-center justify-between" data-testid="action-bar">
                      <div className="text-sm text-slate-600">
                        Approve → kicks off full build (image gen, copy gen, PDF gen, DB load + coupons). Takes 5-10 min.
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={reject} data-testid="reject-week-btn">
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                        <Button onClick={approve} disabled={approving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-week-btn">
                          {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Approve &amp; ship all
                        </Button>
                      </div>
                    </div>
                  )}

                  {activeProposal.status === 'shipped' && activeProposal.ship_result && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
                      <div className="font-semibold text-emerald-900 flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Shipped — {activeProposal.ship_result.products_shipped?.length || 0} products live
                      </div>
                      <div className="text-emerald-800">
                        Storefront: <b>{activeProposal.ship_result.storefront?.name}</b> ·
                        Slug: <code className="text-xs">/store/{activeProposal.ship_result.storefront?.slug}</code>
                      </div>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
